import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ResponsiveGrid from "../grid/responsiveGrid";
import AddIcon from "@mui/icons-material/Add";
import DateIcon from "@mui/icons-material/CalendarMonth";
import CancelIcon from "@mui/icons-material/Cancel";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";

import { useUserAuth } from "../contexts/authContext";
import { CustomPagination } from "../customPagination";
import { ConvertISOToDate, GetTimezone } from "../util";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export default function OrganizationList() {
    const [createOrg, setCreateOrg] = useState(false);
    const [licenseOrg, setLicenseOrg] = useState(null);
    const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [orgColumns, setOrgColumns] = useState([]);
    const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
    const { handleFailedFetch } = useUserAuth();

    const columns = [
        {
            field: "name",
            headerName: "Organization",
            minWidth: 200,
            flex: 1
        },
        {
            type: "dateTime",
            field: "date_created",
            headerName: "Date Created",
            minWidth: 200,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "date_licensed",
            headerName: "Date Licensed",
            minWidth: 250,
            flex: 1,
            renderCell: ({ row }) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: "2px" }}>
                    {row.date_licensed ? (
                        <Typography variant="body1" sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                            {ConvertISOToDate(row.date_licensed)}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                            Not Licensed
                        </Typography>
                    )}
                    <Tooltip title="Add License">
                        <IconButton
                            size="medium"
                            onClick={() => {
                                setLicenseOrg(row);
                                setLicenseDialogOpen(true);
                            }}
                            disabled={row.decommissioned}
                            sx={{ p: 0, pb: "4px", pl: "2px" }}
                        >
                            <DateIcon />
                        </IconButton>
                    </Tooltip>
                    {row.date_licensed && (
                        <Tooltip title="Clear License">
                            <IconButton size="medium" onClick={() => clearLicense(row)} sx={{ p: 0, pb: "4px" }}>
                                <ClearIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        },
        {
            field: "requires_approval",
            headerName: "Requires Approval",
            headerAlign: "center",
            align: "center",
            minWidth: 200,
            flex: 1,
            display: "flex",
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => (
                <Checkbox
                    checked={row.requires_approval}
                    onChange={(e) => toggleApproval(row, e.target.checked)}
                    disabled={row.decommissioned}
                    slotProps={{ input: { "data-testid": "RequiresApproval" } }}
                />
            )
        },
        {
            type: "actions",
            field: "actions",
            headerName: "Decommission",
            width: 150,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key={1}
                    icon={<CancelIcon />}
                    label="Decommission Organization"
                    onClick={() => decommissionOrganization(row)}
                    disabled={row.decommissioned}
                />
            ]
        }
    ];

    const getOrganizations = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/org/get/all", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setOrganizations(data))
            .catch((err) => handleFailedFetch(err));
    };

    useEffect(() => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/services/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setupColumns(data))
            .catch((err) => handleFailedFetch(err));

        getOrganizations();
    }, []);

    const setupColumns = (services) => {
        const serviceColumns = services.map((service) => ({
            field: service.name,
            headerName: formatService(service.name),
            headerAlign: "center",
            align: "center",
            minWidth: 200,
            flex: 1,
            display: "flex",
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const organization = params.row;
                const organizationService = organization?.services?.find((s) => s.name === service.name);
                return (
                    <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Checkbox
                            checked={!!organizationService}
                            onChange={(e) => toggleService(organization, service, e.target.checked)}
                            disabled={organization.decommissioned}
                            slotProps={{ input: { "data-testid": service.name } }}
                        />
                        {organizationService && (
                            <Tooltip
                                title={ConvertISOToDate(organizationService.date_updated)}
                                sx={{ position: "absolute", left: "calc(50% + 16px)", mb: "3px" }}
                            >
                                <DateIcon />
                            </Tooltip>
                        )}
                    </Box>
                );
            }
        }));
        setOrgColumns([...columns.slice(0, 4), ...serviceColumns, ...columns.slice(4)]);
    };

    const formatService = (name) => {
        switch (name) {
            case "COUNTER_UAS":
                return "Counter UAS";
            case "INFRASTRUCTURE":
                return "Infrastructure";
            case "PLAYBACK":
                return "Playback";
            case "OP_MANAGER_DASHBOARD":
                return "Op Manager Dashboard";
            default:
                return "Unknown";
        }
    };

    const toggleService = (organization, service, active) => {
        if (!confirm(`Are you sure you want to toggle ${active ? "on" : "off"} this service?`)) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ organization_id: organization.id, service_id: service.id })
        };
        fetch(active ? "/api/services/insert" : "/api/services/delete", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getOrganizations())
            .catch((err) => handleFailedFetch(err));
    };

    const toggleApproval = (organization, active) => {
        if (!confirm(`Are you sure you want to ${active ? "require" : "no longer require"} approval?`)) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...organization, requires_approval: active })
        };
        fetch("/api/org/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getOrganizations())
            .catch((err) => handleFailedFetch(err));
    };

    const decommissionOrganization = (organization) => {
        if (!confirm("Are you sure you want to decommission this organization? This is irreversible.")) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`/api/org/decommission/${organization.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getOrganizations())
            .catch((err) => handleFailedFetch(err));
    };

    const clearLicense = (organization) => {
        if (!confirm("Are you sure you want to remove the license?")) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...organization, date_licensed: null })
        };
        fetch("/api/org/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getOrganizations())
            .catch((err) => handleFailedFetch(err));
    };

    return (
        <Grid size={16} sx={{ maxWidth: "100% !important", p: 3, width: "100%" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap sx={{ flexGrow: 1 }}>
                Manage Organizations
            </Typography>
            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column" }}>
                <Box sx={{ height: 578 }}>
                    {orgColumns.length > 0 && (
                        <ResponsiveGrid
                            rows={organizations}
                            columns={orgColumns}
                            disableRowSelectionOnClick
                            disableVirtualization
                            pagination
                            slots={{ toolbar: AddOrganization, pagination: CustomPagination }}
                            slotProps={{ toolbar: { setCreateOrg } }}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            sortModel={sortModel}
                            onSortModelChange={(model) => setSortModel(model)}
                        />
                    )}
                </Box>
            </Paper>
            {createOrg && <OrganizationCreator setCreateOrg={setCreateOrg} getOrganizations={getOrganizations} />}
            {licenseDialogOpen && <LicenseDialog licenseOrg={licenseOrg} setLicenseDialogOpen={setLicenseDialogOpen} getOrganizations={getOrganizations} />}
        </Grid>
    );
}

function AddOrganization(props) {
    const handleClick = () => {
        props.setCreateOrg(true);
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick} id="addOrganization">
                Add Organization
            </Button>
        </GridToolbarContainer>
    );
}

function OrganizationCreator(props) {
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const { handleFailedFetch } = useUserAuth();

    const handleCreateOrg = () => {
        if (!name) {
            setNameError("Please enter a valid Organization name.");
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name })
        };
        fetch("/api/org/insert", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => props.getOrganizations())
            .catch((err) => handleFailedFetch(err));
        handleClose();
    };

    const handleClose = () => {
        props.setCreateOrg(false);
    };

    return (
        <Dialog onClose={handleClose} open={true} maxWidth="lg" PaperProps={{ sx: { minWidth: { sm: "300px" }, width: { xs: "100%", sm: "auto" } } }}>
            <DialogTitle>Add Organization</DialogTitle>
            <DialogContent sx={{ overflow: "visible" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, "& > *": { margin: "0 !important" }, pt: "10px" }}>
                    <TextField
                        label="Organization"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={nameError.length > 0}
                        helperText={nameError}
                        fullWidth
                        id="organizationName"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ mr: "auto" }}>
                    Close
                </Button>
                <Button onClick={handleCreateOrg} id="submitOrganization">
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LicenseDialog(props) {
    const [organization] = useState(props.licenseOrg);
    const [licenseDate, setLicenseDate] = useState(props.date_licensed || dayjs());
    const [licenseError, setLicenseError] = useState("");
    const { setSnackbar, handleFailedFetch } = useUserAuth();

    const handleUpdateLicense = () => {
        if (!licenseDate) {
            setLicenseError("Please enter a valid License date.");
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...organization, date_licensed: licenseDate })
        };
        fetch("/api/org/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                props.getOrganizations();
                setSnackbar({ children: "License successfully set", severity: "success" });
            })
            .catch((err) => handleFailedFetch(err));
        handleClose();
    };

    const handleClose = () => {
        props.setLicenseDialogOpen(false);
    };

    return (
        <Dialog onClose={handleClose} open={true} maxWidth="lg" PaperProps={{ sx: { minWidth: { sm: "300px" }, width: { xs: "100%", sm: "auto" } } }}>
            <DialogTitle>Set License Date</DialogTitle>
            <DialogContent sx={{ overflow: "visible" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, "& > *": { margin: "0 !important" }, pt: "10px" }}>
                    <TextField label="Organization" value={organization.name} fullWidth disabled />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label={`License Date (${GetTimezone()}) *`}
                            value={dayjs(licenseDate)}
                            onChange={(value) => setLicenseDate(value)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: licenseError.length > 0,
                                    helperText: licenseError
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ mr: "auto" }}>
                    Close
                </Button>
                <Button onClick={handleUpdateLicense}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
}
